import React, { useState, useMemo } from 'react'
import { useBranding } from '@/contexts/BrandingContext'
import {
  ChevronDown,
  ChevronUp,
  Search,
  Filter,
  X,
  ChevronLeft,
  ChevronRight,
  MoreHorizontal,
} from 'lucide-react'
import { Button } from './button'
import { Input } from './input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select'
import { Checkbox } from './checkbox'
import { TableConfig, TableColumn, TableFilter, TableSort } from '@/types/table'
import { cn } from '@/utils/cn'
import { useAuth } from '@/hooks/useAuth'
import { hasAnyRole } from '@/utils/auth'

interface DataTableProps<T = any> extends TableConfig<T> {
  className?: string
  onRefresh?: () => void
  showStats?: boolean
  showSearch?: boolean
  showFilters?: boolean
  showPagination?: boolean
  showSelection?: boolean
  showActions?: boolean
  compact?: boolean
  striped?: boolean
  hoverable?: boolean
  bordered?: boolean
  loadingText?: string
  emptyText?: string
  emptyIcon?: React.ReactNode
  searchPlaceholder?: string
  filterPlaceholder?: string
  noDataIcon?: React.ReactNode
  loadingIcon?: React.ReactNode
  onServerSideChange?: (params: {
    page: number
    pageSize: number
    sort?: TableSort
    filters: TableFilter[]
    search?: string
  }) => Promise<{ data: T[]; total: number }>
  serverSide?: boolean
}

export function DataTable<T = any>({
  columns,
  data,
  loading = false,
  pagination,
  filters = [],
  sort,
  actions = [],
  rowKey = 'id' as keyof T,
  onSort,
  onFilter,
  onPageChange,
  onRowClick,
  onSelectionChange,
  className,
  emptyText = 'No data available',
  emptyIcon,
  searchable = true,
  searchPlaceholder = 'Search...',
  onSearch,
  selectable = false,
  selectedRowKeys = [],
  onRefresh,
  showStats = true,
  showSearch = true,
  showFilters = true,
  showPagination = true,
  showSelection = true,
  showActions = true,
  striped = true,
  hoverable = true,
  bordered = true,
  loadingText = 'Loading...',
  loadingIcon,
  serverSide = false,
}: DataTableProps<T>) {
  const { branding } = useBranding()
  const { user } = useAuth()

  // Filter columns based on user roles and permissions
  const visibleColumns = useMemo(() => {
    return columns.filter(column => {
      // If no role requirements, show column
      if (!column.requiredRoles || column.requiredRoles.length === 0) {
        return true
      }

      // Check if user has any of the required roles
      return hasAnyRole(user, column.requiredRoles)
    })
  }, [columns, user])

  // Filter actions based on user roles and permissions
  const visibleActions = useMemo(() => {
    return actions.filter(action => {
      // If no role requirements, show action
      if (!action.requiredRoles || action.requiredRoles.length === 0) {
        return true
      }
      // Check if user has any of the required roles
      return hasAnyRole(user, action.requiredRoles)
    })
  }, [actions, user])
  const [searchTerm, setSearchTerm] = useState('')
  const [localFilters, setLocalFilters] = useState<TableFilter[]>(filters)
  const [showFilterDropdown, setShowFilterDropdown] = useState(false)

  // Get row key function
  const getRowKey = (record: T, index: number): string | number => {
    if (typeof rowKey === 'function') {
      return rowKey(record)
    }
    const value = record[rowKey as keyof T]
    return (value as string | number) || index
  }

  // Filter data based on search and filters
  const filteredData = useMemo(() => {
    let result = [...data]

    // Apply search
    if (searchTerm && searchable) {
      result = result.filter(record =>
        columns.some(column => {
          const value = column.dataIndex ? record[column.dataIndex] : undefined
          return String(value).toLowerCase().includes(searchTerm.toLowerCase())
        })
      )
    }

    // Apply filters
    localFilters.forEach(filter => {
      result = result.filter(record => {
        const column = columns.find(col => col.key === filter.key || col.dataIndex === filter.key)
        if (!column || !column.dataIndex) return true

        const recordValue = record[column.dataIndex]
        const filterValue = filter.value

        if (filterValue === null || filterValue === undefined || filterValue === '') {
          return true
        }

        switch (filter.operator) {
          case 'equals':
            return recordValue === filterValue
          case 'contains':
            return String(recordValue).toLowerCase().includes(String(filterValue).toLowerCase())
          case 'startsWith':
            return String(recordValue).toLowerCase().startsWith(String(filterValue).toLowerCase())
          case 'endsWith':
            return String(recordValue).toLowerCase().endsWith(String(filterValue).toLowerCase())
          case 'gt':
            return recordValue > filterValue
          case 'gte':
            return recordValue >= filterValue
          case 'lt':
            return recordValue < filterValue
          case 'lte':
            return recordValue <= filterValue
          case 'in':
            return Array.isArray(filterValue) && filterValue.includes(recordValue)
          case 'notIn':
            return Array.isArray(filterValue) && !filterValue.includes(recordValue)
          default:
            return String(recordValue).toLowerCase().includes(String(filterValue).toLowerCase())
        }
      })
    })

    return result
  }, [data, searchTerm, localFilters, columns, searchable])

  // Sort data
  const sortedData = useMemo(() => {
    if (!sort || !sort.key) return filteredData

    const column = columns.find(col => col.key === sort.key || col.dataIndex === sort.key)
    if (!column || !column.sortable) return filteredData

    return [...filteredData].sort((a, b) => {
      if (column.sorter) {
        return column.sorter(a, b)
      }

      const aValue = column.dataIndex ? a[column.dataIndex] : undefined
      const bValue = column.dataIndex ? b[column.dataIndex] : undefined

      if (aValue === bValue) return 0
      if (aValue === null || aValue === undefined) return 1
      if (bValue === null || bValue === undefined) return -1

      const comparison = aValue < bValue ? -1 : 1
      return sort.direction === 'asc' ? comparison : -comparison
    })
  }, [filteredData, sort, columns])

  // Paginate data (only for client-side pagination)
  const paginatedData = useMemo(() => {
    if (!pagination) return sortedData

    // For server-side pagination, don't slice the data as it's already paginated
    if (serverSide) return sortedData

    const { current, pageSize } = pagination
    const startIndex = (current - 1) * pageSize
    const endIndex = startIndex + pageSize
    return sortedData.slice(startIndex, endIndex)
  }, [sortedData, pagination, serverSide])

  // Handle search
  const handleSearch = (value: string) => {
    setSearchTerm(value)
    onSearch?.(value)
  }

  // Handle filter
  const handleFilter = (
    key: string,
    value: any,
    operator: TableFilter['operator'] = 'contains'
  ) => {
    const newFilters = localFilters.filter(f => f.key !== key)
    if (value !== null && value !== undefined && value !== '') {
      newFilters.push({ key, value, operator })
    }
    setLocalFilters(newFilters)
    onFilter?.(newFilters)
  }

  // Handle sort
  const handleSort = (key: string) => {
    if (!onSort) return

    const newSort: TableSort = {
      key,
      direction: sort?.key === key && sort?.direction === 'asc' ? 'desc' : 'asc',
    }
    onSort(newSort)
  }

  // Handle page change
  const handlePageChange = (page: number) => {
    onPageChange?.(page, pagination?.pageSize || 10)
  }

  // Handle page size change
  const handlePageSizeChange = (pageSize: number) => {
    onPageChange?.(1, pageSize)
  }

  // Handle row selection
  const handleRowSelection = (record: T, checked: boolean) => {
    const key = getRowKey(record, -1)
    const newSelectedKeys = checked
      ? [...selectedRowKeys, key]
      : selectedRowKeys.filter(k => k !== key)

    const newSelectedRows = data.filter(record => newSelectedKeys.includes(getRowKey(record, -1)))

    onSelectionChange?.(newSelectedKeys, newSelectedRows)
  }

  // Handle select all
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allKeys = paginatedData.map((record, index) => getRowKey(record, index))
      const allRows = data.filter(record => allKeys.includes(getRowKey(record, -1)))
      onSelectionChange?.(allKeys, allRows)
    } else {
      onSelectionChange?.([], [])
    }
  }

  // Clear filters
  const clearFilters = () => {
    setLocalFilters([])
    setSearchTerm('')
    onFilter?.([])
    onSearch?.('')
  }

  // Render cell content
  const renderCell = (column: TableColumn<T>, record: T, index: number): React.ReactNode => {
    const value = column.dataIndex ? record[column.dataIndex] : undefined

    if (column.render) {
      return column.render(value, record, index)
    }

    return String(value || '')
  }

  // Render sort icon
  const renderSortIcon = (column: TableColumn<T>) => {
    if (!column.sortable) return null

    const isActive = sort?.key === column.key
    const direction = isActive ? sort?.direction : 'asc'

    return (
      <button
        onClick={() => handleSort(column.key)}
        className="ml-2 p-1 hover:opacity-80 rounded transition-colors"
        style={{ backgroundColor: `${branding.textSecondaryColor}20` }}
      >
        {direction === 'asc' ? (
          <ChevronUp className="h-4 w-4" style={{ color: branding.textColor }} />
        ) : (
          <ChevronDown className="h-4 w-4" style={{ color: branding.textColor }} />
        )}
      </button>
    )
  }

  // Render filter dropdown
  const renderFilterDropdown = () => {
    const filterableColumns = columns.filter(col => col.filterable)

    return (
      <div className="relative">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowFilterDropdown(!showFilterDropdown)}
          className="hover:opacity-80"
          style={{
            backgroundColor: `${branding.surfaceColor}80`,
            borderColor: `${branding.textSecondaryColor}30`,
            color: branding.textColor
          }}
        >
          <Filter className="h-4 w-4 mr-2" />
          Filters
          {localFilters.length > 0 && (
            <span className="ml-2 text-xs px-2 py-1 rounded-full" style={{ backgroundColor: branding.primaryColor, color: branding.textColor }}>
              {localFilters.length}
            </span>
          )}
        </Button>

        {showFilterDropdown && (
          <div className="absolute top-full left-0 mt-2 w-80 backdrop-blur-lg rounded-lg border p-4 z-50" style={{ backgroundColor: `${branding.surfaceColor}90`, borderColor: `${branding.textSecondaryColor}30` }}>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-medium" style={{ color: branding.textColor }}>Filters</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowFilterDropdown(false)}
                  className="hover:opacity-80"
                  style={{ color: branding.textSecondaryColor }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              {filterableColumns.map(column => (
                <div key={column.key} className="space-y-2">
                  <label className="text-sm" style={{ color: branding.textSecondaryColor }}>{column.title}</label>
                  {column.filterType === 'select' && column.filterOptions ? (
                    <Select
                      value={localFilters.find(f => f.key === column.key)?.value || ''}
                      onValueChange={value => handleFilter(column.key, value)}
                    >
                      <SelectTrigger className="hover:opacity-80" style={{ backgroundColor: `${branding.surfaceColor}80`, borderColor: `${branding.textSecondaryColor}30`, color: branding.textColor }}>
                        <SelectValue placeholder={`Filter by ${column.title}`} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="">All</SelectItem>
                        {column.filterOptions.map(option => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      placeholder={`Filter by ${column.title}`}
                      value={localFilters.find(f => f.key === column.key)?.value || ''}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleFilter(column.key, e.target.value)
                      }
                      className="hover:opacity-80"
                      style={{ backgroundColor: `${branding.surfaceColor}80`, borderColor: `${branding.textSecondaryColor}30`, color: branding.textColor }}
                    />
                  )}
                </div>
              ))}

              <div className="flex justify-end space-x-2 pt-2" style={{ borderTopColor: `${branding.textSecondaryColor}30` }}>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  className="hover:opacity-80"
                  style={{ color: branding.textSecondaryColor }}
                >
                  Clear All
                </Button>
                <Button
                  size="sm"
                  onClick={() => setShowFilterDropdown(false)}
                  className="hover:opacity-80"
                  style={{ backgroundColor: branding.primaryColor, color: branding.textColor }}
                >
                  Apply
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  // Render pagination
  const renderPagination = () => {
    if (!pagination || !showPagination) return null

    const { current, pageSize, total, pageSizeOptions = [5, 10, 20, 50] } = pagination
    const totalPages = Math.ceil(total / pageSize)
    const startItem = (current - 1) * pageSize + 1
    const endItem = Math.min(current * pageSize, total)

    return (
      <div className="flex items-center justify-between px-6 py-4 border-t" style={{ backgroundColor: `${branding.surfaceColor}30`, borderTopColor: `${branding.textSecondaryColor}30` }}>
        <div className="flex items-center space-x-4">
          <span className="text-sm" style={{ color: branding.textSecondaryColor }}>
            Showing {startItem} to {endItem} of {total} results
          </span>

          <div className="flex items-center space-x-2">
            <span className="text-sm" style={{ color: branding.textSecondaryColor }}>Rows per page:</span>
            <Select
              value={pageSize.toString()}
              onValueChange={value => handlePageSizeChange(Number(value))}
            >
              <SelectTrigger className="w-20 hover:opacity-80" style={{ backgroundColor: `${branding.surfaceColor}80`, borderColor: `${branding.textSecondaryColor}30`, color: branding.textColor }}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {pageSizeOptions.map(size => (
                  <SelectItem key={size} value={size.toString()}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePageChange(current - 1)}
            disabled={current <= 1}
            className="hover:opacity-80 disabled:opacity-50"
            style={{ backgroundColor: `${branding.surfaceColor}80`, borderColor: `${branding.textSecondaryColor}30`, color: branding.textColor }}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          <div className="flex items-center space-x-1">
            {(() => {
              const maxVisiblePages = 5
              const startPage = Math.max(1, current - Math.floor(maxVisiblePages / 2))
              const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1)
              const adjustedStartPage = Math.max(1, endPage - maxVisiblePages + 1)

              const pages = []

              // Add first page and ellipsis if needed
              if (adjustedStartPage > 1) {
                pages.push(
                  <Button
                    key={1}
                    variant={current === 1 ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => handlePageChange(1)}
                    className={
                      current === 1
                        ? 'hover:opacity-80'
                        : 'hover:opacity-80'
                    }
                    style={
                      current === 1
                        ? { backgroundColor: branding.primaryColor, color: branding.textColor }
                        : { backgroundColor: `${branding.surfaceColor}80`, borderColor: `${branding.textSecondaryColor}30`, color: branding.textColor }
                    }
                  >
                    1
                  </Button>
                )
                if (adjustedStartPage > 2) {
                  pages.push(
                    <span key="ellipsis1" className="px-2" style={{ color: branding.textSecondaryColor }}>
                      ...
                    </span>
                  )
                }
              }

              // Add visible pages
              for (let page = adjustedStartPage; page <= endPage; page++) {
                pages.push(
                  <Button
                    key={page}
                    variant={current === page ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => handlePageChange(page)}
                    className={
                      current === page
                        ? 'hover:opacity-80'
                        : 'hover:opacity-80'
                    }
                    style={
                      current === page
                        ? { backgroundColor: branding.primaryColor, color: branding.textColor }
                        : { backgroundColor: `${branding.surfaceColor}80`, borderColor: `${branding.textSecondaryColor}30`, color: branding.textColor }
                    }
                  >
                    {page}
                  </Button>
                )
              }

              // Add last page and ellipsis if needed
              if (endPage < totalPages) {
                if (endPage < totalPages - 1) {
                  pages.push(
                    <span key="ellipsis2" className="px-2" style={{ color: branding.textSecondaryColor }}>
                      ...
                    </span>
                  )
                }
                pages.push(
                  <Button
                    key={totalPages}
                    variant={current === totalPages ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => handlePageChange(totalPages)}
                    className={
                      current === totalPages
                        ? 'hover:opacity-80'
                        : 'hover:opacity-80'
                    }
                    style={
                      current === totalPages
                        ? { backgroundColor: branding.primaryColor, color: branding.textColor }
                        : { backgroundColor: `${branding.surfaceColor}80`, borderColor: `${branding.textSecondaryColor}30`, color: branding.textColor }
                    }
                  >
                    {totalPages}
                  </Button>
                )
              }

              return pages
            })()}
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePageChange(current + 1)}
            disabled={current >= totalPages}
            className="hover:opacity-80 disabled:opacity-50"
            style={{ backgroundColor: `${branding.surfaceColor}80`, borderColor: `${branding.textSecondaryColor}30`, color: branding.textColor }}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    )
  }

  // Render empty state
  const renderEmptyState = () => (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      {emptyIcon || (
        <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: `${branding.surfaceColor}30` }}>
          <MoreHorizontal className="h-8 w-8" style={{ color: branding.textSecondaryColor }} />
        </div>
      )}
      <h3 className="text-lg font-medium mb-2" style={{ color: branding.textColor }}>No data available</h3>
      <p className="mb-4" style={{ color: branding.textSecondaryColor }}>{emptyText}</p>
      {onRefresh && (
        <Button onClick={onRefresh} className="hover:opacity-80" style={{ backgroundColor: branding.primaryColor, color: branding.textColor }}>
          Refresh
        </Button>
      )}
    </div>
  )

  // Render loading state
  const renderLoadingState = () => (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      {loadingIcon || (
        <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4 animate-spin" style={{ backgroundColor: `${branding.surfaceColor}30` }}>
          <div className="w-8 h-8 border-2 border-t-transparent rounded-full" style={{ borderColor: `${branding.primaryColor} transparent transparent transparent` }}></div>
        </div>
      )}
      <h3 className="text-lg font-medium mb-2" style={{ color: branding.textColor }}>{loadingText}</h3>
    </div>
  )

  return (
    <div
      className={cn('backdrop-blur-lg rounded-2xl border', className)}
      style={{ backgroundColor: `${branding.surfaceColor}30`, borderColor: `${branding.textSecondaryColor}30` }}
    >
      {/* Header */}
      <div className="p-6 border-b" style={{ borderBottomColor: `${branding.textSecondaryColor}30` }}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4">
            {showStats && (
              <div className="text-sm" style={{ color: branding.textSecondaryColor }}>
                <span className="font-medium" style={{ color: branding.textColor }}>{filteredData.length}</span> of{' '}
                <span className="font-medium" style={{ color: branding.textColor }}>{data.length}</span> items
              </div>
            )}
          </div>

          <div className="flex items-center space-x-2">
            {showSearch && searchable && (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4" style={{ color: branding.textSecondaryColor }} />
                <Input
                  placeholder={searchPlaceholder}
                  value={searchTerm}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleSearch(e.target.value)
                  }
                  className="pl-10 w-64 hover:opacity-80"
                  style={{ backgroundColor: `${branding.surfaceColor}80`, borderColor: `${branding.textSecondaryColor}30`, color: branding.textColor }}
                />
              </div>
            )}

            {showFilters && renderFilterDropdown()}

            {onRefresh && (
              <Button
                variant="outline"
                size="sm"
                onClick={onRefresh}
                className="hover:opacity-80"
                style={{ backgroundColor: `${branding.surfaceColor}80`, borderColor: `${branding.textSecondaryColor}30`, color: branding.textColor }}
              >
                Refresh
              </Button>
            )}
          </div>
        </div>

        {/* Active filters */}
        {localFilters.length > 0 && (
          <div className="flex items-center space-x-2">
            <span className="text-sm" style={{ color: branding.textSecondaryColor }}>Active filters:</span>
            {localFilters.map((filter, index) => (
              <div
                key={index}
                className="flex items-center space-x-1 px-2 py-1 rounded-full text-xs"
                style={{ backgroundColor: `${branding.primaryColor}20`, color: branding.primaryColor }}
              >
                <span>
                  {filter.key}: {filter.value}
                </span>
                <button
                  onClick={() => handleFilter(filter.key, '')}
                  className="hover:opacity-80"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
            <button
              onClick={clearFilters}
              className="text-sm hover:opacity-80"
              style={{ color: branding.primaryColor }}
            >
              Clear all
            </button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="border-b" style={{ backgroundColor: `${branding.backgroundColor}80`, borderBottomColor: `${branding.textSecondaryColor}30` }}>
            <tr>
              {selectable && showSelection && (
                <th className="px-6 py-4 text-left">
                  <Checkbox
                    checked={
                      selectedRowKeys.length === paginatedData.length && paginatedData.length > 0
                    }
                    onCheckedChange={handleSelectAll}
                    style={{ borderColor: `${branding.textSecondaryColor}30` }}
                  />
                </th>
              )}
              {visibleColumns.map(column => (
                <th
                  key={column.key}
                  className={cn(
                    'px-6 py-4 text-left text-sm font-medium',
                    column.align === 'center' && 'text-center',
                    column.align === 'right' && 'text-right',
                    column.width && `w-${column.width}`,
                    column.className
                  )}
                  style={{ color: branding.textColor }}
                >
                  <div className="flex items-center">
                    {column.title}
                    {renderSortIcon(column)}
                  </div>
                </th>
              ))}
              {showActions && visibleActions.length > 0 && (
                <th className="px-6 py-4 text-right text-sm font-medium" style={{ color: branding.textColor }}>Actions</th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y" style={{ backgroundColor: `${branding.surfaceColor}30`, borderColor: `${branding.textSecondaryColor}30` }}>
            {loading ? (
              <tr>
                <td
                  colSpan={visibleColumns.length + (selectable ? 1 : 0) + (visibleActions.length > 0 ? 1 : 0)}
                  className="px-6 py-12"
                >
                  {renderLoadingState()}
                </td>
              </tr>
            ) : paginatedData.length === 0 ? (
              <tr>
                <td
                  colSpan={visibleColumns.length + (selectable ? 1 : 0) + (visibleActions.length > 0 ? 1 : 0)}
                  className="px-6 py-12"
                >
                  {renderEmptyState()}
                </td>
              </tr>
            ) : (
              paginatedData.map((record, index) => {
                const key = getRowKey(record, index)
                const isSelected = selectedRowKeys.includes(key)

                return (
                  <tr
                    key={key}
                    className={cn(
                      'transition-colors',
                      striped && index % 2 === 0 && 'opacity-90',
                      hoverable && 'hover:opacity-80',
                      bordered && 'border-b',
                      isSelected && 'opacity-80'
                    )}
                    style={{
                      backgroundColor: isSelected ? `${branding.primaryColor}10` : 'transparent',
                      borderBottomColor: bordered ? `${branding.textSecondaryColor}30` : 'transparent'
                    }}
                    onClick={() => onRowClick?.(record, index)}
                  >
                    {selectable && showSelection && (
                      <td className="px-6 py-4">
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={(checked: boolean) =>
                            handleRowSelection(record, checked)
                          }
                          style={{ borderColor: `${branding.textSecondaryColor}30` }}
                        />
                      </td>
                    )}
                    {visibleColumns.map(column => (
                      <td
                        key={column.key}
                        className={cn(
                          'px-6 py-4 text-sm',
                          column.align === 'center' && 'text-center',
                          column.align === 'right' && 'text-right',
                          column.className
                        )}
                        style={{ color: branding.textColor }}
                      >
                        {renderCell(column, record, index)}
                      </td>
                    ))}
                    {showActions && visibleActions.length > 0 && (
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end space-x-2">
                          {visibleActions
                            .filter(action => (action.visible ? action.visible(record) : true))
                            .map(action => (
                              <Button
                                key={action.key}
                                variant="ghost"
                                size="sm"
                                onClick={e => {
                                  e.stopPropagation()
                                  action.onClick(record)
                                }}
                                disabled={action.disabled ? action.disabled(record) : false}
                                className={cn(
                                  'hover:opacity-80',
                                  action.className
                                )}
                                style={{ color: branding.textSecondaryColor }}
                              >
                                {action.icon}
                                {action.label}
                              </Button>
                            ))}
                        </div>
                      </td>
                    )}
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {renderPagination()}
    </div>
  )
}
